import { useParams } from 'react-router';
import { FolderPlusIcon } from 'lucide-react';
import { Button } from '@monksflow/monks-ui';
import { PanelToggle, SaveToProjectPopover } from '@/shared/ui';
import type { InterviewPersonaLike, SessionsInterviewDeps } from './SessionsLayout.types';
import { InterviewDownloadWrapper } from './InterviewDownloadWrapper';
import { RedesignedChatHeader } from './RedesignedChatHeader';
import { useTranslation } from 'react-i18next';

/**
 * The Audience session's header: the same breadcrumb and renamable session name as every
 * other session, with the interview's own actions beside it.
 */
export function InterviewChatHeader({
  onToggleSidePanel,
  isSidePanelVisible,
  interview,
  chatTitle,
  onRename,
  persona,
}: {
  onToggleSidePanel: () => void;
  isSidePanelVisible: boolean;
  interview: SessionsInterviewDeps;
  chatTitle?: string;
  onRename: (title: string) => void;
  persona: InterviewPersonaLike | null;
}) {
  const { t } = useTranslation('chats');

  const { chatId } = useParams<{ chatId: string }>();
  const saveSession = interview.useSaveSession?.(chatId ?? '') ?? null;

  return (
    <RedesignedChatHeader
      chatTitle={chatTitle}
      onRename={onRename}
      actions={
        <>
          {chatId && saveSession && (
            <SaveToProjectPopover
              resourceId={chatId}
              workspaceId={saveSession.workspaceId}
              onMintArtifact={saveSession.mintArtifact}
            >
              <Button
                variant="outline"
                size="sm"
                aria-label={t('sessions.interviewChatHeader.saveToProject')}
              >
                <FolderPlusIcon size={16} />
              </Button>
            </SaveToProjectPopover>
          )}
          {chatId && <InterviewDownloadWrapper interview={interview} persona={persona} />}
          <PanelToggle
            side="right"
            isOpen={isSidePanelVisible}
            onOpenChange={onToggleSidePanel}
            label={t('sessions.interviewChatHeader.toggleSidePanel')}
          />
        </>
      }
    />
  );
}
